import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import MetaChip from "../../../components/UIToolkit/Badges/MetaChip";
import { AccentIconTile } from "../../../components/UIToolkit/molecules/AccentIconTile";
import { BADGE, FONT_SIZE, LETTER_SPACING } from "../../../theme/tokens";
import { PBJ_ALPHA } from "./colors";
import type { PeriodMode } from "./RecommendationsSidebar";
import type { RecommendationType } from "./recommendations.constants";

type Props = {
	rec: RecommendationType;
	// Theme-derived categorical accent for this lever.
	color: string;
	// Latest Suggest positioned this rec and it's not yet added — shows the "Suggested" pill.
	isSuggested: boolean;
	census: number;
	periodMode: PeriodMode;
	// Day denominators for the math tooltip, resolved for the active period mode by the parent.
	periodDays: number;
	periodWeekendDays: number;
};

// The identity column's starting width beside the slider: wide enough for the longest lever title
// ("Weekend CNA") on one line next to its icon, so titles wrap only on genuinely narrow cards.
const IDENTITY_BASIS_PX = 160;
// The math tooltip is a short paragraph — capped so it wraps as one, not as a full-width banner.
const MATH_TOOLTIP_MAX_WIDTH_PX = 360;

/** The lever card's identity column: icon, title, "Suggested" pill and description, with the lever's math on hover. */
export const RecommendationRowHeader: FC<Props> = ({ rec, color, isSuggested, census, periodMode, periodDays, periodWeekendDays }) => {
	const { t } = useTranslation();
	return (
		<>
			{/* Hover the icon or title to see this rec's math (how added hours map to
		    HPPD across the categories it touches, with real period day-counts and
		    the weekend 2/7 ratio called out where it applies). */}
			<InHouseTooltip
				arrow
				placement="bottom-start"
				slotProps={{ tooltip: { sx: { maxWidth: MATH_TOOLTIP_MAX_WIDTH_PX, p: 1.25 } } }}
				title={
					<Box data-testid={`pbj-model-recommendation-math-${rec.id}`}>
						<Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
							{t(rec.titleKey)}
						</Typography>
						<Typography variant="caption" component="div" sx={{ display: "block", lineHeight: 1.45 }}>
							{t(rec.mathKey, {
								days: periodDays,
								weekendDays: periodWeekendDays,
								ratio: periodWeekendDays > 0 ? (periodDays / periodWeekendDays).toFixed(1) : "—",
								census,
							})}
						</Typography>
						<Typography variant="caption" component="div" sx={{ display: "block", mt: 0.75, opacity: 0.75, fontStyle: "italic" }}>
							{t(periodMode === "quarter" ? "pbj_model.recommendations.period.quarter_hint" : "pbj_model.recommendations.period.qtd_hint")}
						</Typography>
					</Box>
				}
			>
				<Stack
					direction="row"
					spacing={1.5}
					sx={{ alignItems: "center", flex: { xs: 1, sm: `1 1 ${IDENTITY_BASIS_PX}px` }, minWidth: 0, cursor: "help" }}
					data-testid={`pbj-model-recommendation-info-${rec.id}`}
				>
					<AccentIconTile size="xs" color={color} tint={alpha(color, PBJ_ALPHA.TINT)}>
						{rec.icon}
					</AccentIconTile>
					<Box sx={{ minWidth: 0 }}>
						{/* Wraps instead of truncating: on a narrow card the "Suggested" chip drops under the title
						    rather than squeezing it to "CN…". */}
						<Stack direction="row" useFlexGap sx={{ alignItems: "center", flexWrap: "wrap", columnGap: 0.75, rowGap: 0.25, minWidth: 0 }}>
							<Typography variant="body2" sx={{ fontWeight: "bold" }}>
								{t(rec.titleKey)}
							</Typography>
							{/* Same brand tint as the panel's other highlights, so a positioned-but-not-added lever
							    is legible at a glance. */}
							{isSuggested && (
								<MetaChip
									tone="primary"
									label={t("pbj_model.recommendations.suggested_badge")}
									sx={{
										minHeight: BADGE.TAG_CHIP_HEIGHT_SM,
										py: 0,
										fontSize: FONT_SIZE.LABEL_MICRO,
										fontWeight: 700,
										letterSpacing: LETTER_SPACING.LABEL,
										textTransform: "uppercase",
									}}
									dataTestId={`pbj-model-recommendation-suggested-badge-${rec.id}`}
								/>
							)}
						</Stack>
						<Typography variant="caption" sx={{ display: "block", lineHeight: 1.2, mt: 0.25, color: "text.secondary" }}>
							{t(rec.descKey)}
						</Typography>
					</Box>
				</Stack>
			</InHouseTooltip>
		</>
	);
};
